import type { Workspace } from '../../utils/workspaces';
import type { Person } from '../../types/People';

export type PeopleFilter = 'all' | 'login' | 'nologin' | 'archived';

export const FILTER_LABELS: Readonly<Record<PeopleFilter, string>> = {
  all: 'All',
  login: 'Has login',
  nologin: 'No login',
  archived: 'Archived',
};

/** The Login status choices of the Users page's Filters popover; Archived is the bar's own switch. */
export type LoginFilter = Exclude<PeopleFilter, 'archived'>;

export const LOGIN_FILTER_OPTIONS: readonly { value: LoginFilter; label: string }[] = [
  { value: 'all', label: FILTER_LABELS.all },
  { value: 'login', label: FILTER_LABELS.login },
  { value: 'nologin', label: FILTER_LABELS.nologin },
];

/** What the list shows for the bar's two controls: the Archived switch wins and reads its own list. */
export const peopleFilterOf = (archived: boolean, login: LoginFilter): PeopleFilter => (archived ? 'archived' : login);

/** The filters that read the live list; Archived reads its own list. */
export function filterPeople(people: readonly Person[], filter: PeopleFilter): Person[] {
  if (filter === 'login') return people.filter(p => p.login !== null);
  if (filter === 'nologin') return people.filter(p => p.login === null);
  return [...people];
}

/**
 * The projects of the workspace the active project belongs to. A project with
 * no workspace id is only matched with others that have none, so projects of
 * different workspaces are never mixed.
 */
export function workspaceProjects(workspaces: readonly Workspace[], activeProjectId: string | null): Workspace[] {
  const active = workspaces.find(w => w.project_id === activeProjectId);
  if (!active) return [];
  return workspaces.filter(w => w.workspace_id === active.workspace_id);
}

/** Projects this person is not yet on. */
export function projectsToAdd(projects: readonly Workspace[], person: Person): Workspace[] {
  const on = new Set(person.projects.map(p => p.project_id));
  return projects.filter(p => !on.has(p.project_id));
}

/** Teams already used by the workspace's people, offered as suggestions. */
export function knownTeams(people: readonly Person[]): string[] {
  return Array.from(new Set(people.flatMap(p => p.projects.flatMap(pr => pr.teams)))).sort((a, b) => a.localeCompare(b));
}

export function initialsOf(name: string, email: string | null): string {
  const src = name || email || '?';
  return src.split(/[\s@.]+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
}
